/**
 * Seasons — «Monta esta caja».
 *
 * Mete en el carrito las piezas de una caja preparada como líneas normales,
 * para que el cliente pueda quitar y poner. Las 18 cajas sin ventas tienen buen
 * trabajo de curación detrás; esto las convierte en punto de partida en vez de
 * en producto cerrado.
 *
 * POR QUÉ UNA SOLA LLAMADA
 * Todas las piezas van en un único `items[]` de `/cart/add.js`. Si fuesen
 * llamadas sueltas, cada una dispararía `cart:update` y el reconciliador de
 * `premium-box-auto-add.js` entraría a mitad de la operación, con el carrito a
 * medio montar. Con una sola llamada el carrito queda completo antes de que
 * nadie lo mire.
 *
 * QUÉ NO HACE
 * No añade la Caja Diseño. Eso lo decide `premium-box-auto-add.js` por
 * exclusión, y las piezas no están en su lista, así que la añade él solo. Aquí
 * no se calcula ningún importe ni se toca ese fichero.
 *
 * Las piezas agotadas no llegan hasta aquí: Liquid ya las deja fuera del JSON
 * y avisa en pantalla de cuántas se han quedado fuera.
 */

const RUTAS = (() => {
  try {
    return typeof Theme !== 'undefined' && Theme?.routes ? Theme.routes : {};
  } catch {
    return {};
  }
})();

class SeasonsRecipe extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-recipe-add]');
    this.live = this.querySelector('[data-recipe-live]');
    if (!this.button) return;

    this.items = this.#leerPiezas();
    if (!this.items.length) {
      this.button.disabled = true;
      return;
    }

    this.button.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  /** @returns {Array<{id: number, title: string}>} */
  #leerPiezas() {
    const script = this.querySelector('script[data-recipe-items]');
    if (!script?.textContent) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed.filter((p) => Number.isFinite(p?.id)) : [];
    } catch {
      // Sin lista utilizable no se inventa nada: el botón se queda quieto.
      return [];
    }
  }

  /** @param {string} texto */
  #anunciar(texto) {
    if (this.live) this.live.textContent = texto;
  }

  /** @param {string} clave */
  #etiqueta(clave) {
    return this.button?.dataset[clave] || '';
  }

  #onClick = async () => {
    const boton = this.button;
    if (!boton || boton.disabled) return;

    boton.disabled = true;
    boton.textContent = this.#etiqueta('labelBusy');

    try {
      const respuesta = await fetch(RUTAS.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: this.items.map((pieza) => ({ id: pieza.id, quantity: 1 })),
        }),
      });

      if (!respuesta.ok) throw new Error(`cart/add ${respuesta.status}`);

      const carrito = await (await fetch(`${RUTAS.cart_url || '/cart'}.js`)).json();

      boton.textContent = this.#etiqueta('labelDone');
      this.#anunciar(`${this.items.length} · ${this.#etiqueta('labelDone')}`);

      // El tema entero escucha `cart:update`: el icono del carrito, el cajón y
      // la regla de la Caja Diseño. Sin esto nadie se entera.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: carrito,
            sourceId: 'seasons-recipe',
            data: { source: 'seasons-recipe', itemCount: carrito.item_count },
          },
        })
      );
    } catch (error) {
      console.error('Seasons — no se ha podido montar la caja:', error);
      boton.textContent = this.#etiqueta('labelError');
      this.#anunciar(this.#etiqueta('labelError'));
    } finally {
      setTimeout(() => {
        boton.disabled = false;
        boton.textContent = this.#etiqueta('label');
      }, 2500);
    }
  };
}

if (!customElements.get('seasons-recipe')) {
  customElements.define('seasons-recipe', SeasonsRecipe);
}

export default SeasonsRecipe;
